/**
 * Display labels for reward and prize amounts.
 *
 * `reward.metadata.display_name` is the contract's own localized label and
 * always wins — these helpers are the fallback for rewards the admin panel
 * created without one.
 *
 * The V2 contract types `asset` as a free-form string, not an enum. The guide
 * names `LOTTERY_TICKET` concretely and describes "balance rewards such as
 * USDT and TMN" — examples, not a closed set. So this map covers the values
 * that read badly as raw codes, and every other asset renders as its own
 * symbol, which is how the rest of the app shows crypto ("۱۰ USDT").
 *
 * Add a row here when the back end confirms a new non-symbol asset.
 */
import { DecimalString, Task } from './types';

const ASSET_LABELS: Record<string, string> = {
  LOTTERY_TICKET: 'تیکت قرعه‌کشی',
  TMN: 'تومان',
  IRR: 'ریال'
};

const FA_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

/**
 * Persian digit glyphs, applied to the string as sent.
 *
 * This is a character substitution, not a parse — an exact decimal string
 * survives it untouched, which `Number()` would not guarantee.
 */
export const toFaDigits = (value: string): string =>
  value.replace(/[0-9]/g, (digit) => FA_DIGITS[Number(digit)]);

/**
 * Thousands separators on a decimal string: "12345960" -> "12,345,960",
 * "1234.5" -> "1,234.5". Like toFaDigits it only inserts characters and never
 * parses, so large or exact decimal values are kept as sent. Anything that is
 * not a plain decimal is returned unchanged.
 */
export const groupDigits = (value: string): string => {
  const match = /^(-?)(\d+)(\.\d+)?$/.exec(value.trim());
  if (!match) return value;
  const [, sign, whole, fraction = ''] = match;
  return `${sign}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction}`;
};

/** Persian name for an asset code, or the code itself when we have no name. */
export const assetLabel = (asset: string): string =>
  ASSET_LABELS[asset] || asset;

/**
 * "۳ تیکت قرعه‌کشی", "۱,۰۰۰,۰۰۰ تومان" — amount kept exact; only separators
 * and glyphs change.
 */
export const formatRewardLabel = (
  amount: DecimalString,
  asset: string
): string => `${toFaDigits(groupDigits(amount))} ${assetLabel(asset)}`.trim();

/**
 * Splits a label into its leading amount and the rest, e.g.
 * "۲ تیکت قرعه‌کشی هفتگی" -> ["۲", "تیکت قرعه‌کشی هفتگی"], so only the amount
 * is bold. A label that does not start with a number is returned whole.
 */
export const splitAmount = (label: string): [string, string] => {
  const match = label.match(/^\s*([\d۰-۹٠-٩][\d۰-۹٠-٩.,٫]*)\s+(.*)$/);
  return match ? [match[1], match[2]] : ['', label];
};

/** Business timezone: cycle keys and draw dates are Tehran calendar dates. */
export const TEHRAN_TIME_ZONE = 'Asia/Tehran';

const shamsiYear = (date: Date): string =>
  new Intl.DateTimeFormat('fa-IR', {
    year: 'numeric',
    timeZone: TEHRAN_TIME_ZONE
  }).format(date);

/**
 * Day and month in Persian, e.g. "۱۰ دی", in Tehran time. The year is added
 * when it is not the current one, so an older draw date is not ambiguous.
 */
export const shamsiDay = (date: Date): string => {
  const withYear = shamsiYear(date) !== shamsiYear(new Date());
  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    month: 'long',
    ...(withYear && { year: 'numeric' }),
    timeZone: TEHRAN_TIME_ZONE
  }).format(date);
};

/** shamsiDay for an ISO timestamp; a missing or invalid one renders as a dash. */
export const shamsiDayFromIso = (isoDate?: string | null): string => {
  if (!isoDate) return '—';
  const date = new Date(isoDate);
  return Number.isNaN(date.getTime()) ? '—' : shamsiDay(date);
};

/**
 * Start and end of a cycle, derived from its key.
 *
 * `LotteryListItem` carries no cycle timestamps (integration guide §8.4), so a
 * historical draw card has nothing else to date itself with. The keys are
 * Asia/Tehran calendar keys — `DAY:YYYY-MM-DD`, `WEEK:<starting Saturday>`,
 * `MONTH:YYYY-MM` — which fully determine the window.
 *
 * Two caveats. A cycle is clipped to its campaign's own window, so a first or
 * last cycle can be shorter than the range returned here; and `ONCE` carries no
 * dates at all, so it returns null and the card simply omits the row. Replace
 * this with real timestamps if the DTO ever grows them.
 */
export const cycleDateRange = (
  cycleKey: string
): { start: Date; end: Date } | null => {
  // Split on the first colon only: a develop slot key carries a time too
  // (`DAY:2026-09-22T18:20`).
  const key = cycleKey || '';
  const separator = key.indexOf(':');
  if (separator === -1) return null;
  const type = key.slice(0, separator);
  const value = key.slice(separator + 1);
  if (!value) return null;

  // Keys are Tehran calendar dates, so they are read at Tehran's offset (no
  // DST since 2022) rather than in the browser's own timezone.
  const parsed = (local: string): Date | null => {
    const date = new Date(`${local}+03:30`);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const DAY_MS = 24 * 60 * 60 * 1000;

  switch (type) {
    case 'DAY': {
      // Develop-only shortened slots are keyed by their local start time.
      const start = parsed(
        value.includes('T') ? `${value}:00` : `${value}T00:00:00`
      );
      if (!start) return null;
      // The cycle runs to the end of that day, not to its first instant.
      return { start, end: new Date(start.getTime() + DAY_MS - 1) };
    }
    case 'WEEK': {
      const start = parsed(`${value}T00:00:00`);
      if (!start) return null;
      // Saturday through the end of the following Friday.
      return { start, end: new Date(start.getTime() + 7 * DAY_MS - 1) };
    }
    case 'MONTH': {
      const start = parsed(`${value}-01T00:00:00`);
      const [year, month] = value.split('-').map(Number);
      if (!start || !year || !month) return null;
      const next = `${month === 12 ? year + 1 : year}-${String(
        month === 12 ? 1 : month + 1
      ).padStart(2, '0')}-01T00:00:00`;
      const nextStart = parsed(next);
      return nextStart
        ? { start, end: new Date(nextStart.getTime() - 1) }
        : null;
    }
    default:
      return null;
  }
};

/**
 * «هدف: ۱٬۰۰۰٬۰۰۰ تومان» — what the task asks for, shown to a guest in place of
 * progress. Null for a goal with no unit (a yes/no task, target 1), where the
 * number alone says nothing.
 */
export const goalText = (task: Task): string | null => {
  if (!task.goal || !task.goal.unit) return null;
  return `هدف: ${toFaDigits(groupDigits(task.goal.target))} ${assetLabel(
    task.goal.unit
  )}`;
};
